import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { callApiWithOptions, HttpMethod } from "src/services/apiService";
import AdvancedRemediationNav from "./AdvancedRemediationNav";
import {
  formatPhoneInput,
  extractDigits,
  isValidPhoneNumber,
} from "./phoneUtils";
import TrustCallDialerLogs from "./TrustCallDialerLogs";
import WarmupDialerLogs from "./WarmupDialerLogs";
import styles from "./HiyaReputationScan.module.css";


interface HiyaScanResult {
  phone: string;
  businessId: string | null;
  reputationAvailable: string;
  isEnrolled: boolean;
  flagStatus: string | null;
  flagCategory: string | null;
  maturityMetric: string | null;
  desirabilityMetric: string | null;
  engagementMetric: string | null;
  reactionMetric: string | null;
  rawJson: string | null;
  error: string | null;
  durationMs: number;
}

interface HiyaLogEntry {
  apiId: number;
  requestedAt: string;
  endpoint: string;
  httpMethod: string;
  responseStatusCode: number;
  durationMs: number | null;
  operationType: string;
  requestBody: string | null;
  responseBody: string | null;
}

const getMetricColor = (value: string | null): string => {
  if (!value) return "#697077";
  const lower = value.toLowerCase();
  if (lower.includes("high") || lower === "good" || lower === "positive")
    return "#198038";
  if (lower.includes("medium") || lower === "neutral") return "#f1c21b";
  if (lower.includes("low") || lower === "bad" || lower === "negative")
    return "#da1e28";
  return "#0f62fe";
};

const getStatusColor = (code: number): string => {
  if (code >= 200 && code < 300) return "#198038";
  if (code >= 400 && code < 500) return "#f1c21b";
  return "#da1e28";
};

const HiyaReputationScan: React.FC = () => {
  const { phone: phoneParam } = useParams<{ phone?: string }>();
  const navigate = useNavigate();
  const [phoneInput, setPhoneInput] = useState(
    () => (phoneParam ? formatPhoneInput(phoneParam) : "")
  );

  // Keep the URL path in sync with the input so links are shareable.
  // Uses replace:true to avoid pushing a history entry on every keystroke.
  useEffect(() => {
    const digits = extractDigits(phoneInput);
    const target =
      digits.length === 10
        ? `/sysadmin/hiya-scan/${digits}`
        : `/sysadmin/hiya-scan`;
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [phoneInput, navigate]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<HiyaScanResult | null>(null);
  const [logs, setLogs] = useState<HiyaLogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  const [showRawJson, setShowRawJson] = useState(true);

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidPhoneNumber(phoneInput)) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    setError("");
    setResult(null);
    setLogs([]);

    const digits = extractDigits(phoneInput);

    // Run the scan and the log-history fetch as two independent requests rather
    // than awaiting both together. The scan is fast (~300ms); the log-history
    // query is heavier, so blocking the whole view on it makes the page feel
    // slow. Render the scan result immediately, then let the log history stream
    // in afterward behind its own spinner.
    try {
      const scanResponse = await callApiWithOptions({
        endpoint: `sysadmin/advanced-remediation/hiya-scan?phoneNumber=${digits}`,
        method: HttpMethod.GET,
      });
      setResult(scanResponse);
    } catch (err: any) {
      setError(err.message || "Failed to scan phone number");
      setIsLoading(false);
      return; // scan failed — nothing to show, skip the log history fetch
    }
    setIsLoading(false);

    // Load the Hiya API log history separately. A failure here (or a slow
    // query) must never block or break the scan result already on screen.
    setLogsLoading(true);
    try {
      const logsResponse = await callApiWithOptions({
        endpoint: `sysadmin/advanced-remediation/hiya-logs?phoneNumber=${digits}`,
        method: HttpMethod.GET,
      });
      // Backend returns { checks: [...] } or a plain array depending on endpoint
      const logsArray = Array.isArray(logsResponse)
        ? logsResponse
        : Array.isArray(logsResponse?.checks)
          ? logsResponse.checks
          : [];
      setLogs(logsArray);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const reputationCards = result?.isEnrolled
    ? [
        { label: "Flag Status", value: result.flagStatus },
        { label: "Flag Category", value: result.flagCategory },
        { label: "Maturity", value: result.maturityMetric },
        { label: "Desirability", value: result.desirabilityMetric },
        { label: "Engagement", value: result.engagementMetric },
        { label: "Reaction", value: result.reactionMetric },
      ]
    : [];

  return (
    <div className={styles.container}>
      <AdvancedRemediationNav phoneNumber={extractDigits(phoneInput)} />

      {error && (
        <div className="alert alert-danger" role="alert">
          <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
            {error}
          </pre>
        </div>
      )}

      {/* Search Form */}
      <div className="card mb-3">
        <div className="card-body">
          <form onSubmit={handleScan}>
            <div className="d-flex align-items-end gap-3">
              <div>
                <label className="form-label fw-semibold">Phone Number</label>
                <div className="input-group" style={{ width: "240px" }}>
                  <span className="input-group-text">+1</span>
                  <input
                    type="tel"
                    className="form-control"
                    value={phoneInput}
                    onChange={(e) =>
                      setPhoneInput(formatPhoneInput(e.target.value))
                    }
                    placeholder="XXX-XXX-XXXX"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Scanning...
                  </>
                ) : (
                  "Scan Hiya"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* API Error */}
          {result.error && (
            <div className="alert alert-danger">
              <strong>API Error:</strong> {result.error}
            </div>
          )}

          {/* Not Enrolled Info */}
          {!result.isEnrolled && !result.error && (
            <div className="alert alert-info">
              <strong>Not Enrolled.</strong> This phone number is not registered
              in the Hiya Business platform. Only numbers enrolled through
              DNCScrub's Hiya integration will have reputation data available.
              <br />
              <small className="text-muted mt-1 d-block">
                Reputation status:{" "}
                <code>{result.reputationAvailable || "not_found"}</code>
                {result.businessId && (
                  <>
                    {" "}
                    | Business ID: <code>{result.businessId}</code>
                  </>
                )}
              </small>
            </div>
          )}

          {/* Reputation Cards */}
          {result.isEnrolled && (
            <>
              <div className={styles.metaRow}>
                <span>
                  Business ID: <code>{result.businessId || "N/A"}</code>
                </span>
                <span>
                  Status:{" "}
                  <code>{result.reputationAvailable}</code>
                </span>
                <span>
                  API Duration: <strong>{result.durationMs}ms</strong>
                </span>
              </div>
              <div className="row mb-3 g-3">
                {reputationCards.map((card) => (
                  <div key={card.label} className="col-md-4 col-lg-2">
                    <div className={styles.metricCard}>
                      <div className={styles.metricLabel}>{card.label}</div>
                      <div
                        className={styles.metricValue}
                        style={{ color: getMetricColor(card.value) }}
                      >
                        {card.value || "N/A"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Dialer Logs */}
          <TrustCallDialerLogs phoneNumber={extractDigits(phoneInput)} />
          <WarmupDialerLogs phoneNumber={extractDigits(phoneInput)} />

          {/* Raw JSON */}
          <div className="card mb-3">
            <div
              className={`card-header fw-semibold ${styles.collapsible}`}
              onClick={() => setShowRawJson(!showRawJson)}
            >
              Raw Hiya API Response (JSON)
              <span className={styles.chevron}>
                {showRawJson ? "\u25B2" : "\u25BC"}
              </span>
            </div>
            {showRawJson && (
              <div className="card-body p-0">
                <pre className={styles.jsonBlock}>
                  {result.rawJson || "No data"}
                </pre>
              </div>
            )}
          </div>

          {/* Hiya API Log History */}
          <div className="card mb-3">
            <div className="card-header fw-semibold">
              Hiya API Log History{logsLoading ? "" : ` (${logs.length})`}
            </div>
            {logsLoading ? (
              <div className="card-body text-muted d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm" />
                Loading log history...
              </div>
            ) : logs.length === 0 ? (
              <div className="card-body text-muted">
                No Hiya API logs found for this phone number.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th>Date/Time</th>
                      <th>Operation</th>
                      <th>Status</th>
                      <th>Duration</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <React.Fragment key={log.apiId}>
                        <tr>
                          <td>
                            {new Date(log.requestedAt).toLocaleString()}
                          </td>
                          <td>
                            <span className="badge bg-secondary">
                              {log.operationType}
                            </span>
                          </td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: getStatusColor(
                                  log.responseStatusCode,
                                ),
                              }}
                            >
                              {log.responseStatusCode}
                            </span>
                          </td>
                          <td>
                            {log.durationMs != null
                              ? `${log.durationMs}ms`
                              : "N/A"}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() =>
                                setExpandedLogId(
                                  expandedLogId === log.apiId
                                    ? null
                                    : log.apiId,
                                )
                              }
                            >
                              {expandedLogId === log.apiId
                                ? "Hide"
                                : "Show"}
                            </button>
                          </td>
                        </tr>
                        {expandedLogId === log.apiId && (
                          <tr>
                            <td colSpan={5} className="p-0">
                              <div className={styles.logDetail}>
                                <div className={styles.logSection}>
                                  <strong>Endpoint:</strong>{" "}
                                  {log.httpMethod} {log.endpoint}
                                </div>
                                {log.requestBody && (
                                  <div className={styles.logSection}>
                                    <strong>Request:</strong>
                                    <pre className={styles.logJson}>
                                      {tryPrettyPrint(log.requestBody)}
                                    </pre>
                                  </div>
                                )}
                                {log.responseBody && (
                                  <div className={styles.logSection}>
                                    <strong>Response:</strong>
                                    <pre className={styles.logJson}>
                                      {tryPrettyPrint(log.responseBody)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function tryPrettyPrint(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

export default HiyaReputationScan;
